import { useState, useMemo, useCallback, useEffect, type ReactNode } from 'react';
import { useHeader } from '@/contexts';
import { useSegregacaoCalcular } from '@/services/SimplesNacional/Segregacao/hooks/useSegregacaoCalcular';
import { ApuracaoSNContext, type ApuracaoSNData, type SituacaoCompetencia } from '@/contexts';
import { useSegregacaoFiltros } from './useSegregacaoFiltros';
import { fmtMoeda, fmt } from '@/lib/utils/formatters';
import { useSimplesNacional } from '@/contexts/SimplesNacionalContext';
import { useToast } from '@/hooks/use-toast';
import { useStatusFechamento } from '@/services/SimplesNacional/Fechamento/hooks/useStatusFechamento';
import { useFecharCompetencia } from '@/services/SimplesNacional/Fechamento/hooks/useFecharCompetencia';
import { useAbrirCompetencia } from '@/services/SimplesNacional/Fechamento/hooks/useAbrirCompetencia';
import type { SegregacaoCalcularPayload } from '@/services/SimplesNacional/Segregacao/api/types';
import type { FechamentoCompetenciaPayload } from '@/services/SimplesNacional/Fechamento/api/types';

const DADOS_VAZIOS: ApuracaoSNData = {
  competencia: '—',
  status: 'pendente',
  rbt12: 0,
  faixaAtual: '—',
  limiteFaixa: 0,
  percentualFaixa: 0,
  anexo: '—',
  aliquotaNominal: 0,
  parcelaADeduzir: 0,
  aliquotaEfetiva: 0,
  dasARecolher: 0,
  vencimento: '—',
  receitaBrutaTotal: 0,
  receitaTributavel: 0,
  memoriaCalculo: [],
};

interface Props {
  children: ReactNode;
}

export function ApuracaoSNProvider({ children }: Props) {
  const { filtros } = useHeader();
  const { registrarBuscar } = useSimplesNacional();
  const { toast } = useToast();
  const payload = useSegregacaoFiltros();
  const [mostrarZeros, setMostrarZeros] = useState(false);

  // Payload "committed" — só atualiza quando o usuário clica em Buscar
  const [payloadAtivo, setPayloadAtivo] = useState<SegregacaoCalcularPayload | null>(null);

  const { data, isFetching: isLoading, error, refetch } = useSegregacaoCalcular(payloadAtivo);

  const fechamentoPayload = useMemo<FechamentoCompetenciaPayload | null>(() => {
    if (!payload) return null;
    return { CnpjContribuinte: payload.CnpjContribuinte, Ano: payload.Ano, Mes: payload.Mes };
  }, [payload]);

  const { data: statusData, isLoading: isLoadingStatusFechamento } =
    useStatusFechamento(fechamentoPayload);
  const situacaoCompetencia: SituacaoCompetencia = statusData?.data?.Situacao ?? 'Aberto';
  const aguardandoPgdas = statusData?.data?.AguardandoPgdas ?? false;

  const fecharMutation = useFecharCompetencia();
  const abrirMutation = useAbrirCompetencia();

  const buscar = useCallback(
    (forcar = false) => {
      if (situacaoCompetencia === 'Fechado') {
        toast({
          variant: 'destructive',
          title: 'Competência fechada',
          description: 'Abra o período para recalcular a apuração.',
        });
        return;
      }
      const proximoPayload = forcar ? { ...payload!, Forcar: 'S' as const } : payload;
      setPayloadAtivo(proximoPayload);
      if (payloadAtivo !== null) refetch();
    },
    [payload, payloadAtivo, refetch, situacaoCompetencia, toast]
  );

  const buscarSemForcar = useCallback(() => buscar(false), [buscar]);
  const recalcular = useCallback(() => buscar(true), [buscar]);

  // Registra/desregistra o callback no contexto do módulo para o AppHeader usar
  useEffect(() => {
    registrarBuscar(buscarSemForcar);
    return () => registrarBuscar(null);
  }, [buscarSemForcar, registrarBuscar]);

  const fecharCompetencia = useCallback(() => {
    if (!fechamentoPayload) return;
    fecharMutation.mutate(fechamentoPayload, {
      onSuccess: resposta => {
        if (resposta.data.Sucesso) {
          toast({ title: 'Competência fechada' });
        } else {
          toast({
            variant: 'destructive',
            title: 'Não foi possível fechar a competência',
            description: resposta.data.Mensagem ?? resposta.status.mensagem,
          });
        }
      },
      onError: () => {
        toast({ variant: 'destructive', title: 'Erro ao fechar a competência' });
      },
    });
  }, [fechamentoPayload, fecharMutation, toast]);

  const abrirCompetencia = useCallback(() => {
    if (!fechamentoPayload) return;
    abrirMutation.mutate(fechamentoPayload, {
      onSuccess: () => {
        toast({ title: 'Competência aberta' });
      },
      onError: () => {
        toast({ variant: 'destructive', title: 'Erro ao abrir a competência' });
      },
    });
  }, [fechamentoPayload, abrirMutation, toast]);

  const podeCalcular = !!payload;
  const calculado = payloadAtivo !== null;

  const competencia = useMemo(() => {
    const { periodoMes, periodoAno } = filtros;
    if (!periodoMes || !periodoAno) return '—';
    return `${periodoMes}/${periodoAno}`;
  }, [filtros]);

  const dados = useMemo<ApuracaoSNData>(() => {
    if (!data?.data) return { ...DADOS_VAZIOS, competencia };

    const tot = data.data.TOT?.[0];
    const red = data.data.RED?.[0];
    const status = statusData?.data;

    const receitaBrutaTotal = tot?.Total_Faturamento ?? 0;
    const receitaTributavel = tot?.Total_Segregado ?? 0;
    const aliquotaEfetiva = red?.AliquotaEfetiva ?? 0;
    const dasARecolher =
      receitaTributavel > 0 && aliquotaEfetiva > 0
        ? (receitaTributavel * aliquotaEfetiva) / 100
        : 0;

    const vencimentoMes = payloadAtivo?.Mes ? parseInt(payloadAtivo.Mes) : null;
    const vencimentoAno = payloadAtivo?.Ano ? parseInt(payloadAtivo.Ano) : null;
    const vencimento =
      vencimentoMes && vencimentoAno
        ? `20/${String(vencimentoMes).padStart(2, '0')}/${vencimentoAno}`
        : '—';

    const memoriaCalculo = [
      { label: 'Receita Bruta Total', valor: fmtMoeda(receitaBrutaTotal) },
      { label: 'Receita Tributável (Segregada)', valor: fmtMoeda(receitaTributavel) },
      { label: 'Não Segregado', valor: fmtMoeda(tot?.Total_NaoSegregado ?? 0) },
      { label: 'Não Gerador de Receita', valor: fmtMoeda(tot?.Total_NaoGeradorReceita ?? 0) },
      {
        label: 'Alíquota Efetiva',
        formula: 'Tabela Simples Nacional',
        valor: `${fmt(aliquotaEfetiva)}%`,
      },
      {
        label: 'DAS a Recolher',
        formula: 'Receita Tributável × Alíquota Efetiva',
        valor: fmtMoeda(dasARecolher),
      },
    ];

    const statusApuracao =
      situacaoCompetencia === 'Fechado' ? 'fechado' : dasARecolher > 0 ? 'validado' : 'pendente';

    return {
      competencia,
      status: statusApuracao,
      rbt12: status?.RBT12 ?? 0,
      faixaAtual: status?.Faixa ?? '—',
      limiteFaixa: 0,
      percentualFaixa: 0,
      anexo: status?.Anexo ?? '—',
      aliquotaNominal: status?.AliquotaNominal ?? 0,
      parcelaADeduzir: status?.ParcelaADeduzir ?? 0,
      aliquotaEfetiva,
      dasARecolher,
      vencimento,
      receitaBrutaTotal,
      receitaTributavel,
      memoriaCalculo,
    };
  }, [data, competencia, payloadAtivo, statusData, situacaoCompetencia]);

  const value = useMemo(
    () => ({
      dados,
      segregacao: data?.data ?? null,
      isLoading,
      error: error as Error | null,
      mostrarZeros,
      setMostrarZeros,
      buscar: buscarSemForcar,
      recalcular,
      podeCalcular,
      calculado,
      situacaoCompetencia,
      aguardandoPgdas,
      isLoadingStatusFechamento,
      fecharCompetencia,
      abrirCompetencia,
      isFechando: fecharMutation.isPending,
      isAbrindo: abrirMutation.isPending,
    }),
    [
      dados,
      data,
      isLoading,
      error,
      mostrarZeros,
      buscarSemForcar,
      recalcular,
      podeCalcular,
      calculado,
      situacaoCompetencia,
      aguardandoPgdas,
      isLoadingStatusFechamento,
      fecharCompetencia,
      abrirCompetencia,
      fecharMutation.isPending,
      abrirMutation.isPending,
    ]
  );

  return <ApuracaoSNContext.Provider value={value}>{children}</ApuracaoSNContext.Provider>;
}
